import { useCallback } from 'react';

import { queryClient } from '@repo/lib/react-query';
import { useTrpc } from '@repo/lib/trpc-context';
import type { AssetMessage } from '@repo/types/messages';
import type { OrderAsset } from '@repo/types/order';
import { useMutation, useQuery } from '@tanstack/react-query';
import { type TRPCReactRequestOptions } from '@trpc/tanstack-react-query';

import { API } from './utils.api';

const sortByDateTime = (a: AssetMessage, b: AssetMessage) => {
    if (!a.createDateTime || !b.createDateTime) {
        return 0;
    }

    const timestampA = new Date(a.createDateTime).getTime();
    const timestampB = new Date(b.createDateTime).getTime();

    return timestampB - timestampA;
};

export const useOrderMessages = (
    params: {
        assetId: string;
        sortByDateTime?: boolean;
    },
    enabled: boolean,
    trpcOptions?: TRPCReactRequestOptions
) => {
    const trpc = useTrpc();

    return useQuery(
        trpc.messages.list.queryOptions(
            { orderId: params.assetId },
            {
                enabled: enabled,
                select: useCallback(
                    (data: AssetMessage[]) => {
                        return params.sortByDateTime ? [...data].sort(sortByDateTime) : data;
                    },
                    [params.sortByDateTime]
                ),
                trpc: trpcOptions ?? {},
            }
        )
    );
};

export const useAddMessageMutation = () => {
    const trpc = useTrpc();

    return useMutation(
        trpc.messages.add.mutationOptions({
            onMutate: async (newMessage, context) => {
                const queryKey = trpc.messages.list.queryKey({ orderId: newMessage.orderId });
                const hasNewApiQuery = context.client.getQueryCache().find({ queryKey })?.isActive();

                const orderRetrievalQueryKey = [API.assetDetails];

                await context.client.cancelQueries({ queryKey });
                await context.client.cancelQueries({ queryKey: orderRetrievalQueryKey });
                const previousMessages = context.client.getQueryData(queryKey);
                const previousOrderRetrieval = queryClient.getQueryData<OrderAsset>(orderRetrievalQueryKey);

                const newMessageData = {
                    createDateTime: new Date().toISOString(),
                    createdUserId: '',
                    orderMessageId: null,
                    messageType: newMessage.audience,
                    messageText: newMessage.message,
                };

                if (hasNewApiQuery) {
                    context.client.setQueryData(queryKey, (old) => {
                        return [...(old ?? []), newMessageData];
                    });
                }
                queryClient.setQueryData<OrderAsset>(orderRetrievalQueryKey, (old) => {
                    if (!old) {
                        return;
                    }
                    return {
                        assetDetails: {
                            ...old.assetDetails,
                            messages: [...(old.assetDetails.messages ?? []), newMessageData],
                        },
                        assetType: old.assetType,
                    };
                });
                return { previousMessages, previousOrderRetrieval };
            },
            onError: (_error, newMessage, onMutateResult, context) => {
                context.client.setQueryData(
                    trpc.messages.list.queryKey({ orderId: newMessage.orderId }),
                    onMutateResult?.previousMessages
                );
                queryClient.setQueryData<OrderAsset>(
                    [API.assetDetails, newMessage.orderId],
                    onMutateResult?.previousOrderRetrieval
                );
            },
            onSettled: (_data, _error, newMessage, _onMutateResult, context) => {
                void context.client.invalidateQueries({
                    queryKey: trpc.messages.list.queryKey({ orderId: newMessage.orderId }),
                });
                void queryClient.invalidateQueries({
                    queryKey: [API.assetDetails],
                });
            },
        })
    );
};

export const useEditMessageMutation = () => {
    const trpc = useTrpc();
    return useMutation(
        trpc.messages.edit.mutationOptions({
            onMutate: async (newMessage, context) => {
                const queryKey = trpc.messages.list.queryKey({ orderId: newMessage.orderId });

                await context.client.cancelQueries({ queryKey });
                const previousMessages = context.client.getQueryData(queryKey);

                context.client.setQueryData(queryKey, (old) => {
                    return old?.map((message) => {
                        if (message.orderMessageId === newMessage.messageId) {
                            return {
                                ...message,
                                messageText: newMessage.message,
                                createDateTime: new Date().toISOString(),
                            };
                        }
                        return message;
                    });
                });
                return { previousMessages };
            },
            onError: (_error, newMessage, onMutateResult, context) => {
                context.client.setQueryData(
                    trpc.messages.list.queryKey({ orderId: newMessage.orderId }),
                    onMutateResult?.previousMessages
                );
            },
            onSettled: (_data, _error, newMessage, _onMutateResult, context) => {
                void context.client.invalidateQueries({
                    queryKey: trpc.messages.list.queryKey({ orderId: newMessage.orderId }),
                });
                void queryClient.invalidateQueries({
                    queryKey: [API.assetDetails],
                });
            },
        })
    );
};
